import { normalizeDecimalFrameRate } from '../domain/frame-rate'
import type { StageName } from '../domain/stage-name'
import { sanitizeName } from './sanitize-name'

type CommonRenderFileNameParams = {
  exportedAt?: Date
  frameRate: string
  height: number
  projectName: string
  width: number
}

export type BuildRenderFileNameParams = CommonRenderFileNameParams &
  (
    | {
        platformName: string
        stageName: Exclude<StageName, 'Master'>
        versionNumber: number
      }
    | {
        id: string
        stageName: 'Master'
      }
  )

const buildExportDate = (exportedAt: Date): string => {
  // 导出日固定按东八区（北京时间）计，避免运行环境处于 UTC 时跨零点差一天。
  const cst = new Date(exportedAt.getTime() + 8 * 60 * 60 * 1000)
  return [
    cst.getUTCFullYear(),
    String(cst.getUTCMonth() + 1).padStart(2, '0'),
    String(cst.getUTCDate()).padStart(2, '0'),
  ].join('-')
}

const formatFrameRate = (frameRate: string): string => normalizeDecimalFrameRate(frameRate) ?? sanitizeName(frameRate)

// DaVinci 导出文件基名（不含扩展名）；不同 stage 使用各自的业务命名规则。
export const buildRenderFileName = (params: BuildRenderFileNameParams): string => {
  const commonName = [
    buildExportDate(params.exportedAt ?? new Date()),
    sanitizeName(params.projectName),
    `${params.width}x${params.height}`,
    formatFrameRate(params.frameRate),
  ].join('_')

  if (params.stageName === 'Master') {
    return `${commonName}_${params.id}`
  }

  if (params.platformName === 'default') {
    return `V${params.versionNumber}_${commonName}`
  }

  return `V${params.versionNumber}_${commonName}_${sanitizeName(params.platformName)}`
}

// asset 展示名 / OSS 可读名：V{版本号} + 平台后缀（default 平台不带后缀）。
export const buildAssetName = (versionNumber: number, platformName: string): string =>
  `V${versionNumber}${platformName === 'default' ? '' : `_${platformName}`}`
